import {
  type CorrespondenceImportResultDto,
  DOCUMENT_ACCEPT,
  MESSAGE_ATTACHMENTS_MAX,
  type MeDto,
  type MessageChannel,
  type PartyCorrespondenceDto,
  type PartyMessageDto,
  type PartyThreadDetailDto,
  type PartyThreadDto,
} from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, Card, CloseButton, FileButton, Grid, Group, Modal, Paper, ScrollArea, SegmentedControl, Select, Stack, Text, Textarea, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconMail, IconMailDown, IconMessages, IconPaperclip, IconPin, IconPlus, IconSend } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, apiForm } from '../../api';
import { can } from '../../auth';
import { QueryState } from '../../components/QueryState';
import { EmptyState, FilterChips } from '../../components/ui';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk, notifyWarn } from '../../notify';

const CHANNEL_INFO: Record<MessageChannel, { label: string; icon: typeof IconMail; color: string }> = {
  email: { label: 'E-mail', icon: IconMail, color: 'blue' },
  strefa: { label: 'Strefa klienta', icon: IconMessages, color: 'grape' },
};

/** Ikona kanału — e-mail (koperta) albo strefa klienta (dymki); podpowiedź z nazwą. */
export function ChannelIcon({ channel, size = 16 }: { channel: MessageChannel; size?: number }) {
  const c = CHANNEL_INFO[channel];
  const Icon = c.icon;
  return (
    <Tooltip label={c.label} withArrow>
      <Icon size={size} color={`var(--mantine-color-${c.color}-6)`} aria-label={c.label} style={{ flexShrink: 0 }} />
    </Tooltip>
  );
}

const SEND_STATUS: Record<string, { label: string; color: string }> = {
  oczekuje: { label: 'w kolejce do wysłania', color: 'gray' },
  wysylanie: { label: 'wysyłanie…', color: 'gray' },
  wyslany: { label: 'wysłano e-mailem', color: 'green' },
  blad: { label: 'błąd wysyłki — ponawiamy', color: 'orange' },
  blad_trwaly: { label: 'nie wysłano', color: 'red' },
  do_sprawdzenia: { label: 'wysyłka do sprawdzenia', color: 'orange' },
};

const fileSize = (b: number) => (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} kB`);

type Filter = 'wszystkie' | 'nieprzeczytane' | 'email' | 'strefa';

/** Wybrane pliki do wysłania (lista z możliwością usunięcia). */
function Attachments({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  return (
    <Group gap="xs" wrap="wrap">
      <FileButton multiple accept={DOCUMENT_ACCEPT} onChange={(f) => onChange([...files, ...f].slice(0, MESSAGE_ATTACHMENTS_MAX))}>
        {(props) => (
          <Button {...props} size="xs" variant="subtle" leftSection={<IconPaperclip size={14} />}>
            Dołącz pliki
          </Button>
        )}
      </FileButton>
      {files.map((f, i) => (
        <Badge key={`${f.name}-${i}`} variant="light" color="gray" rightSection={<CloseButton size="xs" aria-label="Usuń plik" onClick={() => onChange(files.filter((_, j) => j !== i))} />}>
          {f.name}
        </Badge>
      ))}
    </Group>
  );
}

function Bubble({ m }: { m: PartyMessageDto }) {
  const out = m.direction === 'out';
  const status = m.sendStatus ? SEND_STATUS[m.sendStatus] : null;
  return (
    <Group justify={out ? 'flex-end' : 'flex-start'} wrap="nowrap">
      <Paper
        p="sm"
        radius="md"
        maw="85%"
        withBorder
        style={{ background: out ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-default)', minWidth: 0 }}
      >
        <Group gap={6} mb={4} wrap="wrap">
          <ChannelIcon channel={m.channel} size={14} />
          <Text size="xs" fw={600}>
            {m.fromLabel}
          </Text>
          <Text size="xs" c="dimmed">
            {formatDateTime(m.at)}
            {out && m.email ? ` · do ${m.email}` : ''}
          </Text>
        </Group>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {m.body}
        </Text>
        {m.attachments.length > 0 && (
          <Group gap="xs" mt={6} wrap="wrap">
            {m.attachments.map((a) => (
              <Anchor key={a.id} size="xs" href={`/api/party-message-attachments/${a.id}/file`} target="_blank" rel="noreferrer">
                <IconPaperclip size={12} style={{ verticalAlign: -2 }} /> {a.fileName} ({fileSize(a.size)})
              </Anchor>
            ))}
          </Group>
        )}
        {status && (
          <Text size="xs" mt={4} c={status.color}>
            {status.label}
            {m.sendError ? `: ${m.sendError}` : ''}
          </Text>
        )}
      </Paper>
    </Group>
  );
}

function ThreadView({ threadId, me, cases, onChanged }: { threadId: string; me: MeDto; cases: { id: string; number: string; title: string }[]; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['party-thread', threadId], queryFn: () => api<PartyThreadDetailDto>(`/party-threads/${threadId}`) });
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  useEffect(() => {
    setBody('');
    setFiles([]);
  }, [threadId]);
  // otwarcie wątku oznacza go jako przeczytany — odśwież listę i dzwonek
  useEffect(() => {
    if (q.data) onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.id]);
  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('body', body);
      for (const f of files) form.append('files', f);
      return apiForm<PartyThreadDetailDto>(`/party-threads/${threadId}/reply`, form);
    },
    onSuccess: (t) => {
      qc.setQueryData(['party-thread', threadId], t);
      setBody('');
      setFiles([]);
      notifyOk(t.channel === 'email' ? 'Odpowiedź w kolejce do wysłania e-mailem' : 'Odpowiedź jest w strefie klienta');
      onChanged();
    },
    onError: notifyError,
  });
  const pin = useMutation({
    mutationFn: (caseId: string | null) => api<PartyThreadDetailDto>(`/party-threads/${threadId}/case`, { method: 'PUT', body: { caseId } }),
    onSuccess: (t) => {
      qc.setQueryData(['party-thread', threadId], t);
      notifyOk(t.case ? `Przypięto do sprawy ${t.case.number}` : 'Odpięto od sprawy');
      onChanged();
    },
    onError: notifyError,
  });
  const canWrite = can(me, 'klienci.korespondencja');

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data && (
        <Card withBorder padding="md">
          <Group justify="space-between" align="flex-start" mb="sm" wrap="wrap">
            <div style={{ minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <ChannelIcon channel={q.data.channel} />
                <Text fw={600} style={{ overflowWrap: 'anywhere' }}>
                  {q.data.subject || '(bez tematu)'}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {q.data.channel === 'email'
                  ? [q.data.clientEmail, q.data.company && `skrzynka ${q.data.company.shortName}`].filter(Boolean).join(' · ')
                  : `Strefa klienta sprawy ${q.data.portalCase?.number ?? ''}`}
              </Text>
            </div>
            {canWrite ? (
              <Select
                size="xs"
                w={{ base: '100%', sm: 240 }}
                leftSection={<IconPin size={14} />}
                placeholder="Przypnij do sprawy"
                clearable
                value={q.data.case?.id ?? null}
                data={cases.map((c) => ({ value: c.id, label: `${c.number} — ${c.title}` }))}
                onChange={(v) => pin.mutate(v)}
                disabled={pin.isPending}
                aria-label="Sprawa, do której przypięto wątek"
              />
            ) : (
              q.data.case && (
                <Badge variant="light" leftSection={<IconPin size={12} />}>
                  {q.data.case.number}
                </Badge>
              )
            )}
          </Group>
          <ScrollArea.Autosize mah={520} type="auto" offsetScrollbars>
            <Stack gap="xs">
              {q.data.messages.map((m) => (
                <Bubble key={m.id} m={m} />
              ))}
            </Stack>
          </ScrollArea.Autosize>
          {canWrite && q.data.replyBlocked && (
            <Alert mt="md" color="orange" icon={<IconAlertTriangle size={16} />}>
              {q.data.replyBlocked}
            </Alert>
          )}
          {q.data.canReply && (
            <Stack gap="xs" mt="md">
              <Textarea autosize minRows={3} maxRows={12} placeholder="Treść odpowiedzi" value={body} onChange={(e) => setBody(e.currentTarget.value)} />
              <Group justify="space-between" wrap="wrap">
                <Attachments files={files} onChange={setFiles} />
                <Button size="xs" leftSection={<IconSend size={14} />} loading={send.isPending} disabled={!body.trim()} onClick={() => send.mutate()}>
                  {q.data.channel === 'email' ? 'Wyślij e-mailem' : 'Wyślij do strefy klienta'}
                </Button>
              </Group>
              <Text size="xs" c="dimmed">
                {q.data.channel === 'email'
                  ? `Odpowiedź pójdzie e-mailem do ${q.data.clientEmail} z adresu firmy ${q.data.company?.shortName ?? ''}, w tym samym wątku.`
                  : 'Odpowiedź pojawi się w strefie klienta (zakładka „Wiadomości”); klient dostanie e-mail, że czeka na niego wiadomość — bez jej treści.'}
              </Text>
            </Stack>
          )}
        </Card>
      )}
    </QueryState>
  );
}

function NewMessageModal({ partyId, data, opened, onClose, onCreated }: { partyId: string; data: PartyCorrespondenceDto['compose']; opened: boolean; onClose: () => void; onCreated: (t: PartyThreadDetailDto) => void }) {
  const [channel, setChannel] = useState<MessageChannel>('email');
  const [companyId, setCompanyId] = useState<string | null>(data.companies[0]?.id ?? null);
  const [to, setTo] = useState(data.emails[0] ?? '');
  const [subject, setSubject] = useState('');
  const [caseId, setCaseId] = useState<string | null>(data.portals.length === 1 ? data.portals[0]!.caseId : null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('channel', channel);
      form.append('body', body);
      if (channel === 'email') {
        form.append('companyId', companyId ?? '');
        form.append('to', to);
        form.append('subject', subject);
      } else if (caseId) form.append('caseId', caseId);
      for (const f of files) form.append('files', f);
      return apiForm<PartyThreadDetailDto>(`/parties/${partyId}/correspondence`, form);
    },
    onSuccess: (t) => {
      notifyOk(channel === 'email' ? 'Wiadomość w kolejce do wysłania e-mailem' : 'Wiadomość jest w strefie klienta');
      setBody('');
      setSubject('');
      setFiles([]);
      onCreated(t);
    },
    onError: notifyError,
  });
  const ready = body.trim() && (channel === 'email' ? companyId && to.trim() && subject.trim() : data.portalActive);
  return (
    <Modal opened={opened} onClose={onClose} title="Nowa wiadomość do klienta" size="lg">
      <Stack gap="sm">
        {data.portalActive && (
          <SegmentedControl
            value={channel}
            onChange={(v) => setChannel(v as MessageChannel)}
            data={[
              { value: 'email', label: 'E-mail' },
              { value: 'strefa', label: 'Strefa klienta' },
            ]}
          />
        )}
        {channel === 'email' ? (
          <>
            <Select label="Nadawca (firma)" data={data.companies.map((c) => ({ value: c.id, label: c.shortName }))} value={companyId} onChange={setCompanyId} allowDeselect={false} />
            <Select
              label="Do"
              data={data.emails}
              value={to || null}
              onChange={(v) => setTo(v ?? '')}
              searchable
              allowDeselect={false}
              nothingFoundMessage="Klient nie ma adresu e-mail — dopisz go w danych klienta"
            />
            <TextInput label="Temat" value={subject} onChange={(e) => setSubject(e.currentTarget.value)} maxLength={300} />
          </>
        ) : (
          <Select
            label="Dotyczy sprawy (strefa klienta)"
            data={[{ value: '', label: 'Ogólne (bez sprawy)' }, ...data.portals.map((p) => ({ value: p.caseId, label: p.caseNumber }))]}
            value={caseId ?? ''}
            onChange={(v) => setCaseId(v || null)}
            allowDeselect={false}
          />
        )}
        <Textarea label="Treść" autosize minRows={5} maxRows={14} value={body} onChange={(e) => setBody(e.currentTarget.value)} />
        <Attachments files={files} onChange={setFiles} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button leftSection={<IconSend size={16} />} disabled={!ready} loading={send.isPending} onClick={() => send.mutate()}>
            {channel === 'email' ? 'Wyślij e-mailem' : 'Wyślij do strefy klienta'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ThreadRow({ t, active, onClick }: { t: PartyThreadDto; active: boolean; onClick: () => void }) {
  return (
    <UnstyledButton
      onClick={onClick}
      p="sm"
      style={{
        display: 'block',
        width: '100%',
        borderBottom: '1px solid var(--mantine-color-default-border)',
        background: active ? 'var(--mantine-color-blue-light)' : undefined,
      }}
    >
      <Group gap={6} wrap="nowrap" justify="space-between">
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <ChannelIcon channel={t.channel} size={15} />
          <Text size="sm" fw={t.unread ? 700 : 500} truncate>
            {t.subject || '(bez tematu)'}
          </Text>
        </Group>
        {t.unread > 0 && (
          <Badge size="sm" color="red" circle>
            {t.unread}
          </Badge>
        )}
      </Group>
      <Text size="xs" c="dimmed" lineClamp={1}>
        {t.lastDirection === 'out' ? 'Ty: ' : ''}
        {t.snippet}
      </Text>
      <Group gap={6} mt={2} wrap="wrap">
        <Text size="xs" c="dimmed">
          {formatDateTime(t.lastMessageAt)} · {t.messageCount}
        </Text>
        {t.attachmentCount > 0 && <IconPaperclip size={12} color="var(--mantine-color-dimmed)" aria-label="Załączniki" />}
        {t.case && (
          <Badge size="xs" variant="light" leftSection={<IconPin size={10} />}>
            {t.case.number}
          </Badge>
        )}
      </Group>
    </UnstyledButton>
  );
}

/** Zakładka „Korespondencja” w kartotece klienta (docs/21): e-maile i wiadomości ze strefy klienta, odpowiedzi tym samym kanałem. */
export function PartyCorrespondence({ partyId, me, cases }: { partyId: string; me: MeDto; cases: { id: string; number: string; title: string }[] }) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selected = params.get('watek');
  const [filter, setFilter] = useState<Filter>('wszystkie');
  const [composing, setComposing] = useState(false);
  const q = useQuery({
    queryKey: ['party-correspondence', partyId],
    queryFn: () => api<PartyCorrespondenceDto>(`/parties/${partyId}/correspondence`),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const select = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('watek', id);
        else n.delete('watek');
        return n;
      },
      { replace: true },
    );
  const threads = useMemo(() => {
    const all = q.data?.threads ?? [];
    if (filter === 'nieprzeczytane') return all.filter((t) => t.unread > 0);
    if (filter === 'email' || filter === 'strefa') return all.filter((t) => t.channel === filter);
    return all;
  }, [q.data, filter]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['party-correspondence', partyId] });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const importMails = useMutation({
    mutationFn: () => api<CorrespondenceImportResultDto>(`/parties/${partyId}/correspondence/import`, { method: 'POST' }),
    onSuccess: (r) => {
      refresh();
      const text = [
        r.imported || r.skipped ? `Zaimportowano ${r.imported}, pominięto ${r.skipped} (już w systemie)` : 'Brak maili od klienta w skrzynce',
        r.more ? 'Są jeszcze kolejne — naciśnij ponownie' : '',
        ...r.errors.map((e) => `${e.company}: ${e.message}`),
      ]
        .filter(Boolean)
        .join('. ');
      if (r.errors.length) notifyWarn(text, 'Nie wszystkie skrzynki udało się przeszukać');
      else notifyOk(text);
    },
    onError: notifyError,
  });

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data && (
        <>
          <Group justify="space-between" mb="sm" wrap="wrap">
            <FilterChips
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'wszystkie', label: `Wszystkie (${q.data.threads.length})` },
                { value: 'nieprzeczytane', label: `Nieprzeczytane (${q.data.unread})` },
                { value: 'email', label: 'E-mail' },
                { value: 'strefa', label: 'Strefa klienta' },
              ]}
            />
            {q.data.compose.canWrite && (
              <Group gap="xs">
                <Tooltip label="Pobiera ze skrzynek firm maile od adresów tego klienta; maile już zapisane są pomijane" withArrow multiline w={260}>
                  <Button size="xs" variant="default" leftSection={<IconMailDown size={14} />} loading={importMails.isPending} onClick={() => importMails.mutate()}>
                    Importuj maile od klienta
                  </Button>
                </Tooltip>
                <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setComposing(true)}>
                  Napisz wiadomość
                </Button>
              </Group>
            )}
          </Group>
          {q.data.threads.length === 0 ? (
            <Card withBorder>
              <EmptyState
                icon={<IconMail size={24} />}
                title="Brak korespondencji"
                hint="Tu trafiają e-maile od klienta (z adresów zapisanych w jego danych) i wiadomości ze strefy klienta."
              />
            </Card>
          ) : (
            <Grid gap="md">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card withBorder padding={0}>
                  {threads.length === 0 && <EmptyState compact title="Brak wątków w tym widoku" />}
                  {threads.map((t) => (
                    <ThreadRow key={t.id} t={t} active={t.id === selected} onClick={() => select(t.id)} />
                  ))}
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 8 }}>
                {selected ? (
                  <ThreadView threadId={selected} me={me} cases={cases} onChanged={refresh} />
                ) : (
                  <Card withBorder>
                    <EmptyState compact icon={<IconMessages size={20} />} title="Wybierz wątek z listy" />
                  </Card>
                )}
              </Grid.Col>
            </Grid>
          )}
          <Group gap="md" mt="xs">
            <Group gap={4}>
              <ChannelIcon channel="email" size={14} />
              <Text size="xs" c="dimmed">
                e-mail
              </Text>
            </Group>
            <Group gap={4}>
              <ChannelIcon channel="strefa" size={14} />
              <Text size="xs" c="dimmed">
                strefa klienta
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              Odpowiedź idzie zawsze tym kanałem, którym przyszła wiadomość.
            </Text>
          </Group>
          {composing && (
            <NewMessageModal
              partyId={partyId}
              data={q.data.compose}
              opened
              onClose={() => setComposing(false)}
              onCreated={(t) => {
                setComposing(false);
                refresh();
                select(t.id);
              }}
            />
          )}
        </>
      )}
    </QueryState>
  );
}

/** Wątki przypięte do sprawy — sekcja „Korespondencja z klientem” w dokumentach sprawy. */
export function CaseCorrespondence({ caseId, clientId }: { caseId: string; clientId: string }) {
  const q = useQuery({ queryKey: ['case-correspondence', caseId], queryFn: () => api<PartyThreadDto[]>(`/cases/${caseId}/correspondence`) });
  if (!q.data?.length) return null;
  return (
    <Card withBorder padding={0} mb="md">
      <Group px="md" py="xs" gap={6}>
        <IconMessages size={16} />
        <Text fw={600} size="sm">
          Korespondencja z klientem ({q.data.length})
        </Text>
      </Group>
      {q.data.map((t) => (
        <Group key={t.id} px="md" py={6} gap="xs" wrap="nowrap" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          <ChannelIcon channel={t.channel} size={15} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Anchor component={Link} to={`/klienci/${clientId}?zakladka=korespondencja&watek=${t.id}`} size="sm" fw={500}>
              {t.subject || '(bez tematu)'}
            </Anchor>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {formatDateTime(t.lastMessageAt)} · {t.messageCount} · {t.snippet}
            </Text>
          </div>
          {t.attachmentCount > 0 && (
            <Tooltip label="Załączniki są w zakładce Dokumenty kartoteki klienta">
              <ActionIcon variant="subtle" component={Link} to={`/klienci/${clientId}?zakladka=dokumenty`} aria-label="Załączniki">
                <IconPaperclip size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      ))}
    </Card>
  );
}
